import { FC, useEffect, useState } from 'react';
import SaveButton from '../button/button';
import { Trans } from 'react-i18next';
import { defaultNewPlayer, SweepEditModalProps } from './sweepEditModal.type';
import Close from '../../assets/close.png';
import {
  renderAddNewPlayerButton,
  renderSignatureSection,
  sweepHeader,
} from '../../pages/sweeps/sweepHelper';
import EditPlayerTable from '../../pages/sweeps/editPlayerTable';
import { useUserDetailContext } from '../../service/context/userDetailContext';
import { AuthTokenResponse } from '../../service/service.type';
import {
  Player,
  PlayerUpdate,
  SweepSignature,
  SweepStatus,
  UpdateSweepResponse,
  UserType,
} from '@inplay/models';
import { validateAndDecodeAuthToken } from '../../utils/helper';
import { useSweepsSummary, useSweepUpdate } from '../../hooks/useSweepData';
import ButtonSpinner from '../../assets/buttonLoading/buttonLoading';
import SignatureModal from '../../pages/sweeps/addSignatureModal';
import ConfirmationModal from '../confirmationModal/confirmationModal';
import { UserRole } from '../../pages/sweeps/sweeps.type';
import { apiStatusCode, StatusResponse, Sweep } from '../../utils/helper.type';
import { isUndefined } from 'lodash';
import moment from 'moment';
import { EditPlayer } from '../../pages/sweeps/sweepReport.type';
import AddNewPlayerModal from '../addNewPlayerModal/addNewPlayerModal';
import { useNewPlayers } from '../../service/context/newPlayerContext';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';

const SweepEditModal: FC<SweepEditModalProps> = (props) => {
  const [staffNote, setStaffNote] = useState('');
  const [managerNote, setManagerNote] = useState('');
  const [completedBy, setCompletedBy] = useState<SweepSignature | undefined>(
    props.selectedSweep?.completedBy,
  );
  const [reviewedBy, setReviewedBy] = useState<SweepSignature | undefined>(
    props.selectedSweep?.reviewedBy,
  );
  const [showSignatureModal, setShowSignatureModal] = useState<boolean>(false);
  const [showDeleteSignatureModal, setShowDeleteSignatureModal] =
    useState<boolean>(false);
  const [signatureUserRole, setSignatureUserRole] = useState<UserRole>();
  const [updatedPlayers, setUpdatedPlayers] = useState<EditPlayer[]>([]);
  const [showAddNewPlayerModal, setShowAddNewPlayerModal] =
    useState<boolean>(false);

  const { userDetail } = useUserDetailContext();
  const { updateSweep, updateSweepLoading } = useSweepUpdate();
  const { updateSweepSignature } = useSweepsSummary({
    startTime: undefined,
    endTime: undefined,
    venueBranchIds: [],
  });
  const { newPlayers, setNewPlayers } = useNewPlayers();
  const { isTablet } = useResponsiveLayout();

  const [editPlayer, setEditPlayer] = useState<PlayerUpdate>(defaultNewPlayer);

  useEffect(() => {
    setStaffNote(props.selectedSweep?.completedBy?.notes ?? '');
    setManagerNote(props.selectedSweep?.reviewedBy?.notes ?? '');
  }, [props.selectedSweep]);

  const validAuthToken: AuthTokenResponse | null = validateAndDecodeAuthToken();
  const userType: UserType | undefined = validAuthToken?.type;

  const formatSignaturePayload = (
    sweepSignature: SweepSignature,
    note: string,
  ): SweepSignature => {
    return {
      ...sweepSignature,
      notes: note || '',
    };
  };

  const handleSave = async () => {
    if (updateSweepLoading) {
      console.log('[UpdateSweep] Already in progress... skipping this request');
      return;
    }
    let players: Player[] = updatedPlayers;
    if (isUndefined(props.selectedSweep?._id)) return;
    try {
      console.log(`[ENTRY][UPDATE SWEEP]`);
      const payload: {
        completedBy?: SweepSignature | undefined;
        reviewedBy?: SweepSignature | undefined;
      } = {};

      const completed = isUndefined(completedBy)
        ? undefined
        : formatSignaturePayload(completedBy, staffNote);

      const reviewed = isUndefined(reviewedBy)
        ? undefined
        : formatSignaturePayload(reviewedBy, managerNote);

      if (!isUndefined(completed)) payload.completedBy = completed;
      if (!isUndefined(reviewed)) payload.reviewedBy = reviewed;

      const updateSweepKeyResponse: UpdateSweepResponse | null =
        await updateSweep(
          props.selectedSweep?._id ?? '',
          props.selectedSweep?.version ?? 0,
          payload.completedBy,
          payload.reviewedBy,
          players,
        );

      if (updateSweepKeyResponse) {
        const updateSweepResponse: StatusResponse = {
          statusCode: apiStatusCode.SUCCESS,
          data: updateSweepKeyResponse,
        };

        if (updateSweepResponse.statusCode === apiStatusCode.SUCCESS) {
          setSignatureUserRole(undefined);

          updateSweepSignature(
            props.selectedSweep?._id || '',
            payload.completedBy ?? completedBy,
            payload.reviewedBy ?? reviewedBy,
            players,
          );

          const currentVersion: number = props.selectedSweep?.version ?? 0;
          const newVersion: number = currentVersion + 1;
          if (props.selectedSweep?._id) {
            const updatedSweep = {
              ...props.selectedSweep,
              completedBy:
                payload.completedBy ?? props.selectedSweep.completedBy,
              reviewedBy: payload.reviewedBy ?? props.selectedSweep.reviewedBy,
              version: newVersion,
              players,
            };

            props.setSelectedSweep(updatedSweep);
            setNewPlayers([]);
            props.onCloseEditModal();
          }
        }
      }
    } catch (error: unknown) {
      console.warn('[handleSave] Error during update:', error);
    }
  };

  const handleAddEditSignature = (signature: SweepSignature) => {
    if (!signatureUserRole || !props.selectedSweep) return;

    setShowSignatureModal(false);

    const updatedSweep: Sweep = { ...props.selectedSweep };

    if (signatureUserRole === UserRole.STAFF && signature) {
      setCompletedBy(signature);
      setStaffNote(staffNote ?? '');

      updatedSweep.completedBy = signature;
    }

    if (signatureUserRole === UserRole.MANAGER && signature) {
      setReviewedBy(signature);
      setManagerNote(managerNote);

      updatedSweep.reviewedBy = signature;
    }

    props.setSelectedSweep(updatedSweep);
  };

  const handleCloseEditModal = (): void => {
    setNewPlayers([]);
    props.onCloseEditModal();
  };

  const handleEditSignature = (userRole: UserRole) => {
    setSignatureUserRole(userRole);
    setShowSignatureModal(true);
  };

  const handleAddSignature = (userRole: UserRole) => {
    setSignatureUserRole(userRole);
    setShowSignatureModal(true);
  };

  const handleDeleteSignature = (userRole: UserRole) => {
    setSignatureUserRole(userRole);
    setShowDeleteSignatureModal(true);
  };

  const hasValidSignature = (data?: SweepSignature) => {
    return !!(data?._id && data?.signature);
  };

  const handleConfirmDelete = () => {
    if (!props.selectedSweep) return;

    const extractNotesFromSignature = (
      signature?: SweepSignature,
    ): SweepSignature | undefined => {
      if (!signature?.notes) return undefined;
      return {
        firstName: '',
        lastName: '',
        _id: signature._id,
        signature: '',
        signedDate: new Date(),
        notes: signature.notes,
      };
    };

    const updatedSweep: Sweep = { ...props.selectedSweep };

    if (signatureUserRole === UserRole.MANAGER) {
      updatedSweep.reviewedBy = extractNotesFromSignature(
        updatedSweep.reviewedBy,
      );
    }
    if (signatureUserRole === UserRole.STAFF) {
      updatedSweep.completedBy = extractNotesFromSignature(
        updatedSweep.completedBy,
      );
    }

    props.setSelectedSweep(updatedSweep);
    setShowDeleteSignatureModal(false);
    setSignatureUserRole(undefined);
  };

  const newPlayersValidation: boolean = newPlayers?.every(
    (player: PlayerUpdate) =>
      player?.id?.toString().trim() !== '' &&
      player?.description?.toString().trim() !== '' &&
      player?.startTime?.toString().trim() !== '',
  );

  const isSaveEnabled: boolean =
    (newPlayers.length === 0 || newPlayersValidation) &&
    (hasValidSignature(completedBy) || hasValidSignature(reviewedBy));

  const handleAddNewPlayer = () => {
    setShowAddNewPlayerModal(true);
    setEditPlayer(defaultNewPlayer);
  };

  const handleEditPlayer = (player: PlayerUpdate) => {
    setShowAddNewPlayerModal(true);
    setEditPlayer(player);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
        <div
          className={`bg-white flex flex-col relative overflow-hidden  ${
            isTablet
              ? 'w-[100vw] h-[100vh]'
              : 'w-full h-full max-w-[calc(100%-8rem)] max-h-[calc(100%-3rem)] rounded-md'
          }`}
        >
          <div className="sticky top-0 bg-white z-10 border-b mx-10 py-6 flex justify-between items-start">
            <h2 className="text-lg text-SWEEP_TEXT_COLOR">
              <span className="font-semibold">
                <Trans i18nKey={'sweeps.sweepReport'} />
              </span>{' '}
              - <Trans i18nKey={'sweeps.sweep'} /> #
              {props.selectedSweep?.sweepNumber},{' '}
              {moment(props.selectedSweep?.createdOn).format('DD/MM/YYYY')}
            </h2>
            <div className="flex items-end gap-2">
              <div className="text-lg text-SWEEP_TEXT_COLOR font-semibold mr-5">
                {props.selectedSweep?.type}
              </div>
              <img
                onClick={handleCloseEditModal}
                src={Close}
                alt="Close"
                className="w-5 h-5 cursor-pointer"
              />
            </div>
          </div>

          <div className="flex-1 overflow-auto">
            {!isUndefined(props.selectedSweep) &&
              sweepHeader(props.selectedSweep)}
            {renderSignatureSection(
              userType ?? '',
              props.selectedSweep,
              staffNote,
              setStaffNote,
              managerNote,
              setManagerNote,
              userDetail?.id ?? '',
              handleAddSignature,
              handleDeleteSignature,
              handleEditSignature,
              isTablet,
            )}
            <div className="ml-10 mt-5 mb-3 text-[#151740] font-semibold text-lg">
              <Trans i18nKey={'dashboard.playerToggle.players'} />
            </div>
            <EditPlayerTable
              selectedSweep={props.selectedSweep}
              updatedPlayers={updatedPlayers}
              setUpdatedPlayers={setUpdatedPlayers}
              setNewPlayer={setNewPlayers}
              newPlayer={newPlayers}
              handleEditPlayer={handleEditPlayer}
            />
            {isTablet &&
              !(
                props.loggedUserType === UserType.VENUE_STAFF ||
                props.selectedSweep?.status === SweepStatus.Missed
              ) &&
              renderAddNewPlayerButton(handleAddNewPlayer)}
          </div>
          {!isTablet &&
            !(
              props.loggedUserType === UserType.VENUE_STAFF ||
              props.selectedSweep?.status === SweepStatus.Missed
            ) &&
            renderAddNewPlayerButton(handleAddNewPlayer)}
          <div
            className="sticky bottom-0 border-t p-4 flex justify-center gap-2 bg-white"
            style={{ boxShadow: '0px 0px 30px #2F41A50F' }}
          >
            <SaveButton
              onClick={() => handleSave()}
              disabled={!isSaveEnabled}
              className={` py-2 rounded-full  border-[#D8D8D8] ${
                !isSaveEnabled
                  ? 'bg-[#D5D5D5] text-[#8E8E8E]'
                  : 'bg-primary text-white'
              } ${isTablet ? 'w-[50%] text-lg' : 'w-60 text-base'}`}
            >
              {updateSweepLoading ? (
                <ButtonSpinner />
              ) : (
                <Trans i18nKey={props.footerButtonText} />
              )}
            </SaveButton>
          </div>
        </div>
      </div>

      {showAddNewPlayerModal && (
        <AddNewPlayerModal
          selectedPlayer={editPlayer}
          selectedSweep={props.selectedSweep}
          onCloseSelectPlayerModal={() => setShowAddNewPlayerModal(false)}
        />
      )}

      {showSignatureModal && (
        <SignatureModal
          type={signatureUserRole}
          isOpen={showSignatureModal}
          isBulk={false}
          onClose={() => {
            setShowSignatureModal(false);
            setSignatureUserRole(undefined);
          }}
          selectedSweepRowForModal={props.selectedSweep}
          isEdit={
            signatureUserRole === UserRole.MANAGER
              ? !!props.selectedSweep?.reviewedBy?.signature
              : !!props.selectedSweep?.completedBy?.signature
          }
          usersList={props.usersSummary}
          handleAddEditSignature={handleAddEditSignature}
          loading={false}
          signatureNotes={
            signatureUserRole === UserRole.MANAGER ? managerNote : staffNote
          }
        />
      )}

      {showDeleteSignatureModal && (
        <ConfirmationModal
          onConfirm={handleConfirmDelete}
          showConfirmButton
          isOpen={showDeleteSignatureModal}
          onClose={() => setShowDeleteSignatureModal(false)}
          message={<Trans i18nKey="sweeps.deleteSignature" />}
        />
      )}
    </>
  );
};

export default SweepEditModal;
